import { applyOverlapConstraint, buildOtherMasksUnion } from "@frontend/pages/case-workspace/radiology/lib/mask-overlap"
import { type DragReleaseParams, type NiivueEvent, SLICE_TYPE } from "@niivue/niivue"
import { encodeRLE } from "@niivue/niivue/drawing"
import { applyMask2dToVolume, rasterizePolygon } from "@shared/lib/sam/polygon-rasterizer"
import { extractScribblePoints, extractSliceAsJpeg, projectVoxelBboxToSlice } from "@shared/lib/sam/slice-extractor"
import { SamClient } from "@virdx/sam-client"
import { useCallback, useEffect, useRef, useState } from "react"

import { buildSamMouseEventConfig } from "../mouse-mode"
import type { MaskDrawingConfig } from "../types"

type NiivueInstance = {
  getVolumeData?(voxStart: number[], voxEnd: number[], dataType?: string): [unknown, number[]]
  volumes: Array<{
    name?: string
    dims?: number[]
    cal_min?: number
    cal_max?: number
    img?: ArrayLike<number> | null
  }>
  drawBitmap?: Uint8Array | null
  setDrawingEnabled(enabled: boolean): void
  setMouseEventConfig(config: unknown): void
  addEventListener(type: "dragRelease", listener: (event: NiivueEvent<"dragRelease">) => void): void
  removeEventListener(type: "dragRelease", listener: (event: NiivueEvent<"dragRelease">) => void): void
  refreshDrawing(forceRedraw?: boolean): void
  updateGLVolume(): void
  screenSlices: Array<{
    axCorSag: number
    sliceFrac: number
  }>
}

type SceneLike = {
  getNiivue(viewerIndex: number): NiivueInstance | undefined
}

export interface UseNvSamParams {
  scene: SceneLike
  activeViewerIndex: number
  maskDrawing: MaskDrawingConfig | null
  samMode: boolean
  samScribbleMode: boolean
  /** Returns the original mouse event config to restore when SAM mode exits */
  buildRestoreMouseConfig: () => unknown
  /** Resolve the series ID loaded in a given viewer slot */
  getAssignedSeriesId: (viewerIndex: number) => string | null
  /** Label key for the currently active mask instance (from the page-level mask state) */
  activeMaskLabelKey: string | null
  /** Returns auth headers for authenticated API requests */
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
  /** SAM confidence threshold (0-1). Controls SAM3 server-side filtering and SAM2 client-side quality filter. */
  samConfidence: number
}

export interface UseNvSamReturn {
  samAvailable: boolean
  samPending: boolean
  samError: string | null
  /** Call this from the controller's onMouseUp when samScribbleMode is active instead of normal commit */
  handleScribbleCommit: (nv: NiivueInstance, viewerIndex: number) => void
}

const SAM_PROXY_PATH = "/api/sam"

export function useNvSam({
  scene,
  activeViewerIndex,
  maskDrawing,
  samMode,
  samScribbleMode,
  buildRestoreMouseConfig,
  getAssignedSeriesId,
  activeMaskLabelKey,
  getAuthHeaders,
  samConfidence
}: UseNvSamParams): UseNvSamReturn {
  const [samAvailable, setSamAvailable] = useState(false)
  const [samPending, setSamPending] = useState(false)
  const [samError, setSamError] = useState<string | null>(null)

  const samClientRef = useRef<SamClient | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const maskDrawingRef = useRef(maskDrawing)
  const getAssignedSeriesIdRef = useRef(getAssignedSeriesId)
  const activeMaskLabelKeyRef = useRef(activeMaskLabelKey)
  const samConfidenceRef = useRef(samConfidence)

  useEffect(() => {
    maskDrawingRef.current = maskDrawing
    getAssignedSeriesIdRef.current = getAssignedSeriesId
    activeMaskLabelKeyRef.current = activeMaskLabelKey
    samConfidenceRef.current = samConfidence
  }, [maskDrawing, getAssignedSeriesId, activeMaskLabelKey, samConfidence])

  // Probe SAM availability on mount
  const getAuthHeadersRef = useRef(getAuthHeaders)
  useEffect(() => {
    getAuthHeadersRef.current = getAuthHeaders
  }, [getAuthHeaders])

  useEffect(() => {
    const authFetch = async (url: URL | RequestInfo, init?: RequestInit) => {
      const headers = await getAuthHeadersRef.current()
      return fetch(url, { ...init, headers: { ...init?.headers, ...headers } })
    }
    const client = new SamClient(SAM_PROXY_PATH, authFetch, { confidenceThreshold: samConfidence })
    samClientRef.current = client
    void client.init().then((caps) => {
      setSamAvailable(caps !== null)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- samConfidence synced below
  }, [samConfidence])

  // Keep confidence threshold in sync without recreating the client
  useEffect(() => {
    samClientRef.current?.setConfidenceThreshold(samConfidence)
  }, [samConfidence])

  // Handle SAM prediction pipeline
  const handleDragRelease = useCallback(
    async (params: DragReleaseParams) => {
      const client = samClientRef.current
      const currentMaskDrawing = maskDrawingRef.current
      if (!(client && currentMaskDrawing?.activeMaskInstanceId)) return

      const { axCorSag, voxStart: voxStartRaw, voxEnd: voxEndRaw } = params

      // Only handle 2D slice views
      if (axCorSag !== SLICE_TYPE.AXIAL && axCorSag !== SLICE_TYPE.CORONAL && axCorSag !== SLICE_TYPE.SAGITTAL) {
        return
      }

      const nv = scene.getNiivue(activeViewerIndex)
      if (!nv?.volumes[0]?.dims) return
      if (!nv.drawBitmap) return
      const drawBitmap = nv.drawBitmap

      const voxStart = Array.from(voxStartRaw as ArrayLike<number>)
      const voxEnd = Array.from(voxEndRaw as ArrayLike<number>)

      // Abort any in-flight prediction
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setSamPending(true)
      setSamError(null)

      try {
        // 1. Project drag bbox to 2D slice coordinates
        const { bbox, sliceIndex } = projectVoxelBboxToSlice(voxStart, voxEnd, axCorSag)

        // 2. Extract 2D slice as JPEG
        const extraction = await extractSliceAsJpeg({
          volume: nv.volumes[0]! as Parameters<typeof extractSliceAsJpeg>[0]["volume"],
          sliceType: axCorSag,
          sliceIndex
        })

        if (controller.signal.aborted) return

        // 3. Compute center point of bbox
        const centerX = Math.round((bbox[0] + bbox[2]) / 2)
        const centerY = Math.round((bbox[1] + bbox[3]) / 2)

        // 4. Send to SAM
        const predictions = await client.predict(
          {
            b64img: extraction.b64img,
            pointCoords: [[centerX, centerY]],
            pointLabels: [1],
            bbox
          },
          controller.signal
        )

        if (controller.signal.aborted) return

        // 5. Filter by confidence threshold and pick best prediction
        const minQuality = samConfidenceRef.current
        const filtered = predictions.filter((p) => p.quality >= minQuality)

        if (filtered.length === 0) {
          setSamError(
            predictions.length > 0
              ? `No predictions above confidence threshold (${minQuality.toFixed(2)})`
              : "SAM returned no predictions"
          )
          return
        }

        let best = filtered[0]!
        for (let i = 1; i < filtered.length; i++) {
          if (filtered[i]!.quality > best.quality) {
            best = filtered[i]!
          }
        }

        // 6. Convert Int32Array points to GeoJSON ring
        const ring: [number, number][] = []
        for (let i = 0; i < best.points.length; i += 2) {
          ring.push([best.points[i]!, best.points[i + 1]!])
        }

        // 7. Rasterize polygon to 2D binary mask
        const mask2d = rasterizePolygon([ring], extraction.width, extraction.height)

        // 8. Apply 2D mask to 3D volume bitmap
        const dims = nv.volumes[0]?.dims!
        const volumeDims: [number, number, number] = [dims[1] ?? 1, dims[2] ?? 1, dims[3] ?? 1]

        applyMask2dToVolume(
          mask2d,
          extraction.width,
          extraction.height,
          extraction.pixelToVoxel,
          volumeDims,
          drawBitmap,
          1 // paint value
        )

        // Enforce one-label-per-voxel when the module disallows overlap. SAM
        // fires infrequently, so the union is built on demand here.
        if (currentMaskDrawing.disallowOverlap) {
          applyOverlapConstraint(drawBitmap, buildOtherMasksUnion(nv, currentMaskDrawing.activeMaskInstanceId))
        }

        // 9. Refresh NiiVue rendering
        nv.refreshDrawing(true)
        nv.updateGLVolume()

        // 10. Commit the updated bitmap via the mask drawing callback
        const seriesId = getAssignedSeriesIdRef.current(activeViewerIndex)
        const labelKey = activeMaskLabelKeyRef.current

        if (labelKey && currentMaskDrawing.onBitmapCommit) {
          currentMaskDrawing.onBitmapCommit({
            annotationId: currentMaskDrawing.activeMaskInstanceId!,
            seriesId,
            labelKey,
            rle: encodeRLE(drawBitmap)
          })
        }
      } catch (err) {
        if (controller.signal.aborted) return
        const message = err instanceof Error ? err.message : "SAM prediction failed"
        setSamError(message)
      } finally {
        if (!controller.signal.aborted) {
          setSamPending(false)
        }
      }
    },
    [activeViewerIndex, scene]
  )

  // Wire up / tear down SAM event listeners and mouse config when samMode changes
  useEffect(() => {
    if (!samMode) return

    const nv = scene.getNiivue(activeViewerIndex)
    if (!nv) return

    // Switch mouse config to callbackOnly for bbox drag
    nv.setMouseEventConfig(buildSamMouseEventConfig())

    // Disable drawing (prevent brush strokes during SAM mode)
    nv.setDrawingEnabled(false)

    // Listen for drag release events
    const handler = (event: NiivueEvent<"dragRelease">) => {
      void handleDragRelease(event.detail)
    }
    nv.addEventListener("dragRelease", handler)

    return () => {
      nv.removeEventListener("dragRelease", handler)
      // Restore original mouse config
      nv.setMouseEventConfig(buildRestoreMouseConfig())
      setSamPending(false)
      abortRef.current?.abort()
    }
  }, [samMode, activeViewerIndex, scene, handleDragRelease, buildRestoreMouseConfig])

  // SAM scribble: extract painted points from drawBitmap, send to SAM, replace with result
  const handleScribbleCommit = useCallback((nv: NiivueInstance, viewerIndex: number) => {
    const client = samClientRef.current
    const currentMaskDrawing = maskDrawingRef.current
    if (!(client && currentMaskDrawing?.activeMaskInstanceId)) return
    if (!(nv.drawBitmap && nv.volumes[0]?.dims)) return

    const dims = nv.volumes[0]?.dims!
    const volumeDims: [number, number, number] = [dims[1] ?? 1, dims[2] ?? 1, dims[3] ?? 1]

    // Determine which slice the user is viewing from screenSlices[0]
    const slice = nv.screenSlices[0]
    if (!slice) return
    const axCorSag = slice.axCorSag as SLICE_TYPE
    if (axCorSag !== SLICE_TYPE.AXIAL && axCorSag !== SLICE_TYPE.CORONAL && axCorSag !== SLICE_TYPE.SAGITTAL) return

    // Determine slice index from sliceFrac
    const throughDimSize =
      axCorSag === SLICE_TYPE.AXIAL ? volumeDims[2] : axCorSag === SLICE_TYPE.CORONAL ? volumeDims[1] : volumeDims[0]
    const sliceIndex = Math.round(slice.sliceFrac * (throughDimSize - 1))

    // Extract scribble points from the drawBitmap
    const points = extractScribblePoints(nv.drawBitmap, volumeDims, axCorSag, sliceIndex)
    if (points.length === 0) return

    // Save the pre-scribble bitmap so we can restore if SAM fails,
    // and clear the scribble from the bitmap before applying SAM result
    const drawBitmap = nv.drawBitmap
    if (!drawBitmap) return
    const bitmapCopy = new Uint8Array(drawBitmap)

    // Abort any in-flight prediction
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setSamPending(true)
    setSamError(null)

    void (async () => {
      try {
        // Extract slice as JPEG
        const extraction = await extractSliceAsJpeg({
          volume: nv.volumes[0]! as Parameters<typeof extractSliceAsJpeg>[0]["volume"],
          sliceType: axCorSag,
          sliceIndex
        })

        if (controller.signal.aborted) return

        // Send points to SAM (no bbox)
        const predictions = await client.predict(
          {
            b64img: extraction.b64img,
            pointCoords: points,
            pointLabels: points.map(() => 1) // all positive
          },
          controller.signal
        )

        if (controller.signal.aborted) return

        // Filter by confidence threshold and pick best
        const minQuality = samConfidenceRef.current
        const filtered = predictions.filter((p) => p.quality >= minQuality)

        if (filtered.length === 0) {
          // Restore the pre-scribble bitmap on failure
          drawBitmap.set(bitmapCopy)
          nv.refreshDrawing(true)
          setSamError(
            predictions.length > 0
              ? `No predictions above confidence threshold (${minQuality.toFixed(2)})`
              : "SAM returned no predictions"
          )
          return
        }

        let best = filtered[0]!
        for (let i = 1; i < filtered.length; i++) {
          if (filtered[i]!.quality > best.quality) best = filtered[i]!
        }

        // Convert to ring
        const ring: [number, number][] = []
        for (let i = 0; i < best.points.length; i += 2) {
          ring.push([best.points[i]!, best.points[i + 1]!])
        }

        // Rasterize and apply — start from pre-scribble state (remove the scribble strokes)
        drawBitmap.set(bitmapCopy)
        const mask2d = rasterizePolygon([ring], extraction.width, extraction.height)
        applyMask2dToVolume(
          mask2d,
          extraction.width,
          extraction.height,
          extraction.pixelToVoxel,
          volumeDims,
          drawBitmap,
          1
        )

        // Enforce one-label-per-voxel when the module disallows overlap.
        if (currentMaskDrawing.disallowOverlap) {
          applyOverlapConstraint(drawBitmap, buildOtherMasksUnion(nv, currentMaskDrawing.activeMaskInstanceId))
        }

        nv.refreshDrawing(true)
        nv.updateGLVolume()

        // Commit
        const seriesId = getAssignedSeriesIdRef.current(viewerIndex)
        const labelKey = activeMaskLabelKeyRef.current
        if (labelKey && currentMaskDrawing.onBitmapCommit) {
          currentMaskDrawing.onBitmapCommit({
            annotationId: currentMaskDrawing.activeMaskInstanceId!,
            seriesId,
            labelKey,
            rle: encodeRLE(drawBitmap)
          })
        }
      } catch (err) {
        if (controller.signal.aborted) return
        // Restore bitmap on error
        drawBitmap.set(bitmapCopy)
        nv.refreshDrawing(true)
        setSamError(err instanceof Error ? err.message : "SAM scribble prediction failed")
      } finally {
        if (!controller.signal.aborted) setSamPending(false)
      }
    })()
  }, [])

  return { samAvailable, samPending, samError, handleScribbleCommit }
}
